import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, Trophy, Users, MapPin, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { format } from 'date-fns';

interface FeaturedItem {
  id: string;
  item_type: string;
  item_id: string;
  feature_type: string;
  priority: number;
  end_date: string;
}

const Featured = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [featuredCoaches, setFeaturedCoaches] = useState<FeaturedItem[]>([]);
  const [featuredTeams, setFeaturedTeams] = useState<FeaturedItem[]>([]);
  const [featuredVenues, setFeaturedVenues] = useState<FeaturedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeaturedItems();
  }, []);

  const loadFeaturedItems = async () => {
    try {
      const now = new Date().toISOString();

      // Load featured coaches
      const { data: coaches } = await supabase
        .from('featured_items')
        .select('*')
        .eq('item_type', 'coach')
        .eq('is_active', true)
        .gte('end_date', now)
        .order('priority', { ascending: false })
        .limit(6);

      setFeaturedCoaches(coaches || []);

      // Load featured teams
      const { data: teams } = await supabase
        .from('featured_items')
        .select('*')
        .eq('item_type', 'team')
        .eq('is_active', true)
        .gte('end_date', now)
        .order('priority', { ascending: false })
        .limit(6);

      setFeaturedTeams(teams || []);

      // Load featured venues
      const { data: venues } = await supabase
        .from('featured_items')
        .select('*')
        .eq('item_type', 'venue')
        .eq('is_active', true)
        .gte('end_date', now)
        .order('priority', { ascending: false })
        .limit(6);

      setFeaturedVenues(venues || []);
    } catch (error) {
      console.error('Error loading featured items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFeatureIcon = (type: string) => {
    switch (type) {
      case 'spotlight':
        return <Star className="h-5 w-5 text-yellow-500" />;
      case 'featured':
        return <Trophy className="h-5 w-5 text-orange-500" />;
      default:
        return <Star className="h-5 w-5 text-yellow-500" />;
    }
  };

  const renderFeaturedItem = (item: FeaturedItem) => (
    <Card key={item.id} className="bg-gray-900 border-gray-700 hover:border-yellow-600 transition-colors group">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {getFeatureIcon(item.feature_type)}
            <Badge variant="secondary" className="bg-yellow-600/20 text-yellow-500 border-yellow-600/30">
              Featured
            </Badge>
          </div>
          {item.priority > 1 && (
            <Badge variant="secondary" className="bg-orange-600/20 text-orange-500">
              Premium
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <CardTitle className="text-white group-hover:text-sports-green transition-colors">
          {item.item_type.charAt(0).toUpperCase() + item.item_type.slice(1)} #{item.item_id.substring(0, 8)}
        </CardTitle>
        
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Calendar className="h-4 w-4" />
          <span>Featured until {format(new Date(item.end_date), 'MMM dd, yyyy')}</span>
        </div>

        <Button
          onClick={() => navigate(`/${item.item_type}s/${item.item_id}`)}
          className="w-full mt-4"
          variant="outline"
        >
          View Profile
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen flex flex-col bg-black">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}

      <main className="flex-1 container mx-auto px-4 py-8 pb-24 md:pb-8 mt-24">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600/20 rounded-full mb-4">
            <Star className="h-5 w-5 text-yellow-500" />
            <span className="text-yellow-500 font-semibold">Featured Spotlights</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Top Performers & Rising Stars
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Discover the best coaches, teams, and venues in the community
          </p>
        </div>

        {/* Featured Content */}
        <Tabs defaultValue="coaches" className="w-full">
          <TabsList className="bg-gray-900 border-gray-700 mb-8">
            <TabsTrigger value="coaches">
              <Users className="h-4 w-4 mr-2" />
              Coaches
            </TabsTrigger>
            <TabsTrigger value="teams">
              <Trophy className="h-4 w-4 mr-2" />
              Teams
            </TabsTrigger>
            <TabsTrigger value="venues">
              <MapPin className="h-4 w-4 mr-2" />
              Venues
            </TabsTrigger>
          </TabsList>

          <TabsContent value="coaches">
            {loading ? (
              <div className="text-center text-gray-400 py-12">Loading featured coaches...</div>
            ) : featuredCoaches.length === 0 ? (
              <Card className="bg-gray-900 border-gray-700">
                <CardContent className="py-12 text-center">
                  <p className="text-gray-400">No featured coaches at the moment</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredCoaches.map(renderFeaturedItem)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="teams">
            {loading ? (
              <div className="text-center text-gray-400 py-12">Loading featured teams...</div>
            ) : featuredTeams.length === 0 ? (
              <Card className="bg-gray-900 border-gray-700">
                <CardContent className="py-12 text-center">
                  <p className="text-gray-400">No featured teams at the moment</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredTeams.map(renderFeaturedItem)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="venues">
            {loading ? (
              <div className="text-center text-gray-400 py-12">Loading featured venues...</div>
            ) : featuredVenues.length === 0 ? (
              <Card className="bg-gray-900 border-gray-700">
                <CardContent className="py-12 text-center">
                  <p className="text-gray-400">No featured venues at the moment</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredVenues.map(renderFeaturedItem)}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <Card className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border-yellow-600 mt-12">
          <CardContent className="p-8 text-center">
            <Star className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Want to be Featured?</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Boost your visibility and reach more players, teams, and fans. Feature your profile or venue today!
            </p>
            <Button
              onClick={() => navigate('/account-upgrade')}
              className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
            >
              Learn More About Featured Spotlights
            </Button>
          </CardContent>
        </Card>
      </main>

      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default Featured;
